import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GraduationCap, ArrowLeft } from 'lucide-react';
import { supabase } from '../database/supabase';

const StudentRegistrationPage = () => {
  const [formData, setFormData] = useState({
    studentId: '',
    name: '',
    email: '',
    campus: '',
    department: '',
    level: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const getLevelOptions = () => {
    switch(formData.department) {
      case 'BED': return ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6'];
      case 'JHS': return ['Grade 7', 'Grade 8', 'Grade 9', 'Grade 10'];
      case 'SHS': return ['Grade 11', 'Grade 12'];
      case 'HED': return ['College'];
      default: return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('student_accounts')
        .insert([{
          student_id: formData.studentId,
          password_hash: formData.password, // In production, hash this
          name: formData.name,
          email: formData.email,
          campus: formData.campus,
          department: formData.department,
          level: formData.level,
          status: 'pending'
        }])
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('Student ID already exists');
        }
        throw error;
      }

      toast.success('Registration successful! Waiting for admin approval.');
      setTimeout(() => navigate('/teacher-evaluation'), 2000);
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate('/teacher-evaluation')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </button>

        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-8 h-8 text-blue-800" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Request Access</h1>
            <p className="text-gray-600 mt-2">Register for teacher evaluation access</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Student ID *</label>
              <input
                type="text"
                className="form-input"
                value={formData.studentId}
                onChange={(e) => setFormData({...formData, studentId: e.target.value})}
                placeholder="e.g., 2024001"
                required
              />
            </div>

            <div>
              <label className="form-label">Full Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Juan Dela Cruz"
                required
              />
            </div>

            <div>
              <label className="form-label">Email *</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="student@school.edu"
                required
              />
            </div>

            <div>
              <label className="form-label">Campus *</label>
              <select
                className="form-input"
                value={formData.campus}
                onChange={(e) => setFormData({...formData, campus: e.target.value})}
                required
              >
                <option value="">Select Campus</option>
                <option value="Gubat">Gubat</option>
                <option value="Main">Main</option>
              </select>
            </div>

            <div>
              <label className="form-label">Department *</label>
              <select
                className="form-input"
                value={formData.department}
                onChange={(e) => setFormData({...formData, department: e.target.value, level: ''})}
                required
              >
                <option value="">Select Department</option>
                <option value="BED">BED - Basic Education</option>
                <option value="JHS">JHS - Junior Highschool</option>
                <option value="SHS">SHS - Senior Highschool</option>
                <option value="HED">HED - Higher Education</option>
              </select>
            </div>

            <div>
              <label className="form-label">Level *</label>
              <select
                className="form-input"
                value={formData.level}
                onChange={(e) => setFormData({...formData, level: e.target.value})}
                disabled={!formData.department}
                required
              >
                <option value="">Select Level</option>
                {getLevelOptions().map(lvl => <option key={lvl} value={lvl}>{lvl}</option>)}
              </select>
            </div>

            <div>
              <label className="form-label">Password *</label>
              <input
                type="password"
                className="form-input"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="Minimum 6 characters"
                required
              />
            </div>

            <div>
              <label className="form-label">Confirm Password *</label>
              <input
                type="password"
                className="form-input"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                placeholder="Re-enter password"
                required
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Your account will be pending until approved by an administrator. 
                You will be able to login once your account is approved.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Submitting...' : 'Request Access'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/teacher-evaluation')}
                className="text-blue-800 hover:text-red-900 font-medium"
              >
                Login here
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentRegistrationPage;
